import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Loader2, Download, KeyRound, AlertTriangle, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { changePassword, deleteAccount, exportData, updateProfile } from "@/lib/api";
import { levelLabel } from "@/lib/labels";

const INDUSTRIES = [
  { id: "retail", key: "industryRetail" },
  { id: "hospital_cleaning", key: "industryHospitalClean" },
  { id: "hospitality", key: "industryHospitality" },
  { id: "restaurant", key: "industryRestaurant" },
  { id: "call_center", key: "industryCallCenter" },
  { id: "travel", key: "industryTravel" },
  { id: "office", key: "industryOffice" },
  { id: "warehouse_logistics", key: "industryWarehouseLogistics" },
  { id: "healthcare_support", key: "industryHealthcareSupport" },
  { id: "construction_trades", key: "industryConstructionTrades" },
  { id: "landscaping", key: "industryLandscaping" },
  { id: "other", key: "industryOther" },
];
const PARTNERS = [
  { id: "customer", key: "partnerCustomer" },
  { id: "coworker", key: "partnerCoworker" },
  { id: "supervisor", key: "partnerSupervisor" },
  { id: "patient_visitor", key: "partnerPatient" },
];
const NEEDS = [
  { id: "ask_clarification", key: "needAskClar" },
  { id: "understand_request", key: "needUnderstand" },
  { id: "explain_issue", key: "needExplain" },
  { id: "ask_permission", key: "needAskPerm" },
  { id: "report_status", key: "needReportStatus" },
  { id: "request_help", key: "needAskHelp" },
  { id: "handle_disagreement", key: "needDisagree" },
  { id: "phone_conversations", key: "needPhone" },
  { id: "talk_to_supervisor", key: "needTalkSup" },
];

export default function Settings() {
  const { tr, lang } = useLang();
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [level, setLevel] = useState(user?.level || "basic");
  const [industry, setIndustry] = useState(user?.work_profile?.industry || "");
  const [otherDesc, setOtherDesc] = useState(user?.work_profile?.other_description || "");
  const [roleLabel, setRoleLabel] = useState(user?.work_profile?.role_label || "");
  const [tasksText, setTasksText] = useState((user?.work_profile?.tasks || []).join("\n"));
  const [partners, setPartners] = useState(new Set(user?.work_profile?.partners || []));
  const [needs, setNeeds] = useState(new Set(user?.work_profile?.needs || []));
  const [timePref, setTimePref] = useState(user?.work_profile?.time_pref_min || 10);
  const [saving, setSaving] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const toggleSet = (set, id, setter) => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id); else n.add(id);
    setter(n);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const u = await updateProfile({
        level,
        work_profile: {
          industry,
          other_description: industry === "other" ? otherDesc.trim().slice(0, 600) : "",
          role_label: roleLabel,
          tasks: tasksText.split("\n").map((t) => t.trim()).filter(Boolean).slice(0, 8),
          partners: [...partners],
          needs: [...needs],
          time_pref_min: Number(timePref) || 10,
          language_pref: "es",
        },
      });
      setUser(u);
      toast.success(tr("saved"));
    } catch (err) { toast.error(tr("errorGeneric")); }
    finally { setSaving(false); }
  };

  const doChangePassword = async () => {
    if (curPw.length < 1 || newPw.length < 8) { toast.error(tr("formError")); return; }
    setPwSubmitting(true);
    try {
      const r = await changePassword(curPw, newPw);
      localStorage.setItem("wpe_token", r.access_token);
      setCurPw(""); setNewPw("");
      toast.success(tr("passwordChanged"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("errorGeneric"));
    } finally { setPwSubmitting(false); }
  };

  const doExport = async () => {
    try {
      const data = await exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `listotalk-ai-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(tr("exportedNote"));
    } catch (err) { toast.error(tr("errorGeneric")); }
  };

  const doDeleteAcc = async () => {
    setConfirmDel(false);
    try {
      await deleteAccount();
      logout();
      navigate("/");
    } catch (err) { toast.error(tr("errorGeneric")); }
  };

  return (
    <AppShell>
      <section className="px-5 pt-4 pb-10 space-y-5" data-testid="settings-screen">
        <h1 className="text-[22px] font-bold text-[rgb(var(--wpe-navy))]" data-testid="settings-title">
          {tr("settingsTitle")}
        </h1>

        {user?.is_admin && (
          <button
            type="button"
            onClick={() => navigate("/admin")}
            className="wpe-card w-full text-left flex items-center gap-3 hover:border-[rgb(var(--wpe-teal))] transition-colors"
            data-testid="settings-admin-entry"
          >
            <span className="rounded-full bg-[rgb(var(--wpe-teal-subtle))] p-2 shrink-0">
              <ShieldCheck size={18} className="text-[rgb(var(--wpe-teal-hover))]" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[14px] font-bold text-[rgb(var(--wpe-navy))]">
                {tr("adminEntryCardTitle")}
              </span>
              <span className="block text-[11px] text-[rgb(var(--wpe-muted))]">
                {tr("adminEntryCardSub")}
              </span>
            </span>
            <ArrowRight size={16} className="text-[rgb(var(--wpe-muted))] shrink-0" />
          </button>
        )}

        <div className="wpe-card space-y-3">
          <h2 className="wpe-eyebrow">{tr("workProfile")}</h2>
          <div>
            <label className="wpe-eyebrow block mb-1">{tr("onboardingLevel")}</label>
            <div className="flex gap-2 flex-wrap">
              {["basic", "intermediate", "advanced"].map((d) => (
                <button key={d} type="button" onClick={() => setLevel(d)} className={`text-[11px] px-3 py-1 rounded-full border ${level === d ? "bg-[rgb(var(--wpe-teal))] text-white border-[rgb(var(--wpe-teal))]" : "bg-white text-[rgb(var(--wpe-slate))] border-[rgb(var(--wpe-border))]"}`} data-testid={`settings-level-${d}`}>{levelLabel(lang, d)}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="wpe-eyebrow block mb-1">{tr("industry")}</label>
            <div className="grid grid-cols-2 gap-2">
              {INDUSTRIES.map((i) => (
                <button key={i.id} type="button" onClick={() => setIndustry(i.id)} className={`py-2 px-3 rounded-lg border text-[12px] text-left ${industry === i.id ? "border-[rgb(var(--wpe-teal))] bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))]" : "border-[rgb(var(--wpe-border))] bg-white text-[rgb(var(--wpe-slate))]"}`} data-testid={`settings-industry-${i.id}`}>{tr(i.key)}</button>
              ))}
            </div>
          </div>
          {industry === "other" && (
            <div className="space-y-1" data-testid="settings-other-desc-wrap">
              <label className="wpe-eyebrow block" htmlFor="settings-other-desc">{tr("otherJobDescribeLabel")}</label>
              <p className="text-[11px] text-[rgb(var(--wpe-muted))]">{tr("otherJobDescribeHelp")}</p>
              <textarea
                id="settings-other-desc"
                className="wpe-input min-h-[90px] resize-none text-[13px]"
                placeholder={tr("otherJobDescribePlaceholder")}
                value={otherDesc}
                onChange={(e) => setOtherDesc(e.target.value.slice(0, 600))}
                maxLength={600}
                data-testid="settings-other-desc-input"
              />
            </div>
          )}
          <label className="block">
            <span className="wpe-eyebrow block mb-1">{tr("roleLabel")}</span>
            <input className="wpe-input text-[13px]" value={roleLabel} onChange={(e) => setRoleLabel(e.target.value.slice(0, 120))} data-testid="settings-role" />
          </label>
          <label className="block">
            <span className="wpe-eyebrow block mb-1">{tr("tasks")}</span>
            <textarea className="wpe-input min-h-[60px] text-[13px]" value={tasksText} onChange={(e) => setTasksText(e.target.value)} data-testid="settings-tasks" />
          </label>
          <div>
            <span className="wpe-eyebrow block mb-1">{tr("partners")}</span>
            <div className="flex flex-wrap gap-2">
              {PARTNERS.map((p) => (
                <button key={p.id} type="button" onClick={() => toggleSet(partners, p.id, setPartners)} className={`text-[11px] px-3 py-1 rounded-full border ${partners.has(p.id) ? "bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))] border-[rgb(var(--wpe-teal))]" : "bg-white text-[rgb(var(--wpe-slate))] border-[rgb(var(--wpe-border))]"}`} data-testid={`settings-partner-${p.id}`}>{tr(p.key)}</button>
              ))}
            </div>
          </div>
          <div>
            <span className="wpe-eyebrow block mb-1">{tr("needsHeader")}</span>
            <div className="flex flex-wrap gap-2">
              {NEEDS.map((n) => (
                <button key={n.id} type="button" onClick={() => toggleSet(needs, n.id, setNeeds)} className={`text-[11px] px-3 py-1 rounded-full border ${needs.has(n.id) ? "bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))] border-[rgb(var(--wpe-teal))]" : "bg-white text-[rgb(var(--wpe-slate))] border-[rgb(var(--wpe-border))]"}`} data-testid={`settings-need-${n.id}`}>{tr(n.key)}</button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="wpe-eyebrow block mb-1">{tr("timePref")} (min)</span>
            <select className="wpe-input text-[13px]" value={timePref} onChange={(e) => setTimePref(e.target.value)} data-testid="settings-time-pref">
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
            </select>
          </label>
          <button type="button" onClick={saveProfile} disabled={saving} className="wpe-cta w-full" data-testid="settings-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            {tr("saveContinue")}
          </button>
        </div>

        <div className="wpe-card space-y-2">
          <h2 className="wpe-eyebrow flex items-center gap-1"><KeyRound size={12} /> {tr("changePassword")}</h2>
          <input type="password" className="wpe-input text-[13px]" placeholder={tr("currentPassword")} value={curPw} onChange={(e) => setCurPw(e.target.value)} autoComplete="current-password" data-testid="settings-cur-pw" />
          <input type="password" className="wpe-input text-[13px]" placeholder={tr("newPassword")} value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" data-testid="settings-new-pw" />
          <button type="button" onClick={doChangePassword} disabled={pwSubmitting} className="wpe-secondary w-full text-[13px]" data-testid="settings-change-pw-btn">
            {pwSubmitting ? <Loader2 size={12} className="animate-spin" /> : null}
            {tr("changePassword")}
          </button>
        </div>

        <div className="wpe-card space-y-2">
          <h2 className="wpe-eyebrow"><Download size={12} className="inline mr-1" />{tr("exportMyData")}</h2>
          <button type="button" onClick={doExport} className="wpe-secondary w-full text-[13px]" data-testid="settings-export-btn">
            <Download size={12} /> {tr("exportMyData")}
          </button>
        </div>

        <PilotDisclaimer />

        <div className="wpe-card space-y-2">
          <h2 className="wpe-eyebrow flex items-center gap-1"><AlertTriangle size={12} /> {tr("dangerZoneTitle")}</h2>
          <button type="button" onClick={() => setConfirmDel(true)} className="text-[13px] text-[rgb(var(--wpe-danger))] font-semibold underline" data-testid="settings-delete-account-btn">
            {tr("deleteAccount")}
          </button>
        </div>

        {confirmDel && (
          <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6" data-testid="settings-confirm-delete">
            <div className="wpe-card max-w-sm w-full space-y-3">
              <h3 className="text-[15px] font-semibold text-[rgb(var(--wpe-navy))]">{tr("confirmDeleteAccount")}</h3>
              <div className="flex gap-2">
                <button className="wpe-secondary flex-1" onClick={() => setConfirmDel(false)}>{tr("no")}</button>
                <button className="wpe-cta flex-1" onClick={doDeleteAcc}><Trash2 size={12} /> {tr("yes")}</button>
              </div>
            </div>
          </div>
        )}
      </section>
    </AppShell>
  );
}
